import type { GanOauth } from 'types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGoogleLogin } from '@react-oauth/google';
import { useClient } from 'hooks/useClient';
import { useAuth } from 'hooks/useAuth';
import { toast } from '@/hooks/use-toast';
import { config } from 'config';

export const useGoogleAccounts = () => {
  const apiClient = useClient();
  const ganClient = useClient(config.GAN_URL);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: googleAccounts } = useQuery<GanOauth[]>({
    queryKey: ['GOOGLE_OAUTH_ACCOUNTS'],
    retry: false,
    queryFn: async () => {
      queryClient.invalidateQueries({ queryKey: ['GAN_PROPERTIES'] });
      return ganClient.get('/google-oauth');
    },
  });

  const onConnect = useGoogleLogin({
    flow: 'auth-code',
    accessType: 'offline',
    scope: [
      'https://www.googleapis.com/auth/analytics.readonly',
      'https://www.googleapis.com/auth/analytics.edit',
      'https://www.googleapis.com/auth/userinfo.profile',
      'https://www.googleapis.com/auth/userinfo.email',
    ].join(' '),
    onSuccess: async (codeResponse: any) => {
      await ganClient.post('/google-oauth/connect', codeResponse);
      queryClient.invalidateQueries({ queryKey: ['GOOGLE_OAUTH_ACCOUNTS'] });
    },
  } as Parameters<typeof useGoogleLogin>[0]);

  const refresh = async (email: string) => {
    const isClientOrganization = user.company.type !== 'Agency';
    await ganClient.post(`/google-oauth/refresh`, {
      email,
      isClientOrganization,
    });

    toast({
      title: 'Your request is being processed',
      description: 'You will receive an email when the process is complete',
    });
  };

  const disconnect = async (email: string) => {
    await apiClient.delete(`/google-accounts/${email}`);
    queryClient.invalidateQueries({ queryKey: ['GOOGLE_ACCOUNTS'] });
    queryClient.invalidateQueries({ queryKey: ['ORGANIZATIONS'] });
  };

  return {
    googleAccounts,
    onConnect,
    disconnect,
    refresh,
  };
};

export type useGoogleAccountsReturnType = ReturnType<typeof useGoogleAccounts>;
